import "@/assets/styles/table.order.css";

import BasicTable from "@/components/shared/BasicTable";
import { ColDef } from "ag-grid-community";
import classNames from "classnames";
import { useCallback, useMemo } from "react";
import { Button } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  IntegrationLogSearchParams,
  IntegrationLogVo,
} from "@/types/integration";
import { useIntegrationLogs } from "./helper";
import LogDetail from "./info/LogDetail";

interface LogsTableProps {
  className?: string;
  cols: ColDef<IntegrationLogVo>[];
  query: IntegrationLogSearchParams;
  onQueryChange: (query: IntegrationLogSearchParams) => void;
}

export default function IntegrationLogsTable(props: LogsTableProps) {
  const { cols, className, query, onQueryChange } = props;
  const { data: logs, refetch, isFetching } = useIntegrationLogs(query);

  const onQueryParamsChange = useCallback(
    (params: Partial<IntegrationLogSearchParams>) => {
      onQueryChange(params);
    },
    [onQueryChange],
  );

  const actionColumn = useMemo(() => {
    return {
      headerName: "Action",
      minWidth: 65,
      maxWidth: 65,
      resizable: false,
      pinned: "right",
      suppressMenu: true,
      cellRenderer: () => {
        return (
          <div className="flex gap-1">
            <Button icon={<InfoCircleOutlined />} />
          </div>
        );
      },
    } as ColDef<IntegrationLogVo>;
  }, []);

  const columns = useMemo(() => {
    return [...cols, actionColumn];
  }, [cols, actionColumn]);

  const rowDetailRender = useCallback((data: IntegrationLogVo | undefined) => {
    if (!data) return null;
    const logId = data.id;
    return (
      <div className="pb-2 pt-1 px-3 bg-gray-100 h-full overflow-y-auto">
        <LogDetail logId={logId} />
      </div>
    );
  }, []);

  return (
    <BasicTable
      className={classNames("order-table", className)}
      columns={columns}
      page={logs}
      loading={isFetching}
      getRowId={(row) => `${row.id}`}
      enableRowDetail={true}
      enablePagination={true}
      rowHeight={52}
      onRefreshData={refetch}
      onSortChange={(sorts, dirs) => {
        onQueryParamsChange({ sorts, dirs });
      }}
      onPaginationChange={(page, size) => {
        onQueryParamsChange({ size, page });
      }}
      detailRowHeight={240}
      rowDetailRender={rowDetailRender}
    />
  );
}
